import { useEffect, useState } from 'preact/hooks';
import { api } from '../api.js';
import { navigate, scheduleVersion } from '../store.js';
import { __ } from '../i18n.js';
import { formatOccurrenceTime, scheduleIconName } from '../lib/schedule.js';
import { useVisibleRefresh } from '../lib/refresh.js';
import Icon from '../components/Icon.jsx';

/**
 * HH-7 dashboard card: "Vandaag op het weekritme" — today's occurrences,
 * grouped per gezinslid (plan §3, the brief's card 1).
 *
 * Reads the existing GET /schedule/week rather than a new endpoint: the
 * server already computes the whole week through the HH-6 pure class and
 * flags which day is today, so picking that day here costs one array
 * lookup and adds no second implementation of "what is on today". The
 * roster comes from the existing GET /people; if only that call fails the
 * card still renders, just without names.
 *
 * Fails soft, like every card here: any error hides the card entirely
 * (the WP-3 HomeScreen open-count precedent — a landing screen must never
 * greet you with an error), never blocks the hero below it.
 */
export default function ScheduleTodayCard() {
  const [day, setDay] = useState(null);
  const [people, setPeople] = useState([]);
  const [failed, setFailed] = useState(false);
  const version = scheduleVersion.value; // refresh after any schedule mutation
  // ...and whenever the app returns to the foreground: the server froze
  // today's date into this payload when it was built.
  const visibleTick = useVisibleRefresh();

  useEffect(() => {
    let cancelled = false;

    api.schedule
      .week(0)
      .then((res) => {
        if (cancelled) return;
        setDay((res.days || []).find((d) => d.is_today) || null);
        setFailed(false);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    api.people
      .list()
      .then((res) => {
        if (!cancelled) setPeople(res.people || []);
      })
      .catch(() => {
        // Names are decoration here, not the card's reason to exist.
      });

    return () => {
      cancelled = true;
    };
  }, [version, visibleTick]);

  if (failed || !day) return null;

  // Only what is actually on: a skipped occurrence is dimmed in the week
  // grid (where it can be undone), but on a dashboard it would just be
  // noise about something that is not happening.
  const occurrences = (day.occurrences || []).filter((o) => !o.skipped);

  const groups = [];
  for (const occurrence of occurrences) {
    const person = occurrence.person_id
      ? people.find((p) => Number(p.id) === Number(occurrence.person_id))
      : null;
    const key = person ? `p${person.id}` : 'household';
    let group = groups.find((g) => g.key === key);
    if (!group) {
      group = {
        key,
        name: person ? person.name : __('Iedereen', 'etm'),
        colorRole: person ? person.color_role : 'neutral',
        occurrences: [],
      };
      groups.push(group);
    }
    group.occurrences.push(occurrence);
  }

  return (
    <section class="etm-dashboard-card">
      <button type="button" class="etm-dashboard-card__header" onClick={() => navigate('week')}>
        <span class="etm-dashboard-card__icon">
          <Icon name="calendar_month" size={20} />
        </span>
        <span class="etm-dashboard-card__title">{__('Vandaag op het weekritme', 'etm')}</span>
        <Icon name="chevron_right" size={20} />
      </button>

      {occurrences.length === 0 ? (
        <p class="etm-dashboard-card__empty">{__('Niets gepland vandaag.', 'etm')}</p>
      ) : (
        <div class="etm-dashboard-groups">
          {groups.map((group) => (
            <div class="etm-dashboard-group" key={group.key}>
              <span class={`etm-person-dot etm-person-dot--${group.colorRole}`} aria-hidden="true" />
              <span class="etm-dashboard-group__name">{group.name}</span>
              <ul class="etm-dashboard-group__items">
                {group.occurrences.map((occurrence) => (
                  <li key={`${occurrence.item_id}-${occurrence.date}`}>
                    <Icon name={scheduleIconName(occurrence.icon)} size={16} />
                    <span class="etm-dashboard-group__label">{occurrence.title}</span>
                    <span class="etm-dashboard-group__time">
                      {formatOccurrenceTime(occurrence) || __('Hele dag', 'etm')}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
